import { useEffect, useMemo, useRef, useState } from 'react';
import { useWarehouseGrid } from '@modules/WarehouseMap/hooks/useWarehouseGrid';
import { useWarehouseRacks } from '@modules/WarehouseMap/hooks/useWarehouseRacks';
import { usePickingRoute } from '@modules/WarehouseMap/hooks/usePickingRoute';
import type { RouteResult } from '@modules/WarehouseMap/models/types';
import type { Preparation } from '../models/PreparationModels';

// TODO: Ideally the GET /preparations/{id} endpoint should internally call
// the warehouse picking-route bounded context and return the optimized route
// in a single response — avoiding the extra network round-trip from the frontend.
// For now we fetch grid + racks + compute route as separate calls.

const WAREHOUSE_ID = 1; // TODO: get from preparation or user context
const EMPTY_RACKS: never[] = [];

export const usePickingRouteMap = (preparation: Preparation | null) => {
  const grid = useWarehouseGrid(WAREHOUSE_ID);
  const racks = useWarehouseRacks(WAREHOUSE_ID);
  const pickingRoute = usePickingRoute(WAREHOUSE_ID);
  const pickingRouteRef = useRef(pickingRoute);
  pickingRouteRef.current = pickingRoute;
  const [route, setRoute] = useState<RouteResult | null>(null);

  const hasComputedRoute = useRef(false);

  useEffect(() => {
    if (!preparation || hasComputedRoute.current) return;

    const locationIds = new Set<number>();
    for (const line of preparation.lines) {
      for (const loc of line.suggested_locations ?? []) {
        locationIds.add(loc.id);
      }
    }

    hasComputedRoute.current = true;

    if (locationIds.size >= 2) {
      pickingRouteRef.current.mutate(
        { location_ids: Array.from(locationIds) },
        {
          onSuccess: (data) => setRoute(data),
          onError: () => setRoute(null),
        },
      );
    }
  }, [preparation]);

  const selectedCells = useMemo(() => {
    const cells = new Set<string>();
    if (route) {
      for (const stop of route.stops) {
        cells.add(`${stop.rack_row}:${stop.rack_col}`);
      }
    }
    return cells;
  }, [route]);

  return {
    grid: grid.data ?? null,
    racks: racks.data ?? EMPTY_RACKS,
    route,
    selectedCells,
    isLoading: grid.isLoading || racks.isLoading || pickingRoute.isPending,
    error: grid.error || racks.error || pickingRoute.error || null,
  };
};
